(() => {
  'use strict';

  // Shopify may evaluate a section's asset again. Keep one page-level controller.
  if (document.documentElement.dataset.atHomeStoryReady === 'true') return;
  document.documentElement.dataset.atHomeStoryReady = 'true';

  const media = typeof window.matchMedia === 'function'
    ? window.matchMedia('(min-height: 500px) and (prefers-reduced-motion: no-preference)')
    : null;
  const stories = new Map();
  const clamp = (value) => Math.min(1, Math.max(0, value));
  const passive = { passive: true };
  let frame = null;
  let watchingMedia = false;
  let watchingViewport = false;
  let motionEnabled = false;

  function cancelFrame() {
    if (frame === null) return;
    if (typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(frame);
    frame = null;
  }

  function restorePhotos(root) {
    root.querySelectorAll('.at-cinema__fallback template[data-story-media]').forEach((template) => {
      template.replaceWith(template.content.cloneNode(true));
    });
  }

  function prefetchNext(story) {
    // Do not chain downloads: only the chapter actually reached by scroll can
    // anticipate its neighbour, after its own photograph is ready.
    if (!story.alive || story.failed || !motionEnabled || story.target < 0) return;
    if (story.photos[story.target]?.status === 'ready') loadPhoto(story, story.target + 1, 'low');
  }

  function loadPhoto(story, index, priority = 'high') {
    const photo = story.photos[index];
    if (!photo || !story.managed || !story.alive || story.failed) return;
    if (photo.status !== 'idle') {
      if (photo.image && priority === 'high' && photo.status !== 'ready') photo.image.fetchPriority = 'high';
      return;
    }
    const template = photo.frame.querySelector('template[data-story-media]');
    const fragment = template?.content.cloneNode(true);
    const image = fragment?.querySelector('img') || photo.frame.querySelector('img');
    photo.image = image;
    if (!image) {
      photo.status = 'error';
      return;
    }
    photo.status = 'loading';
    if (template) {
      image.loading = 'eager';
      image.fetchPriority = priority;
    }
    function cleanup() {
      image.removeEventListener('load', loaded);
      image.removeEventListener('error', failed);
    }
    function failed() {
      if (!story.alive) return;
      cleanup();
      photo.status = 'error';
      schedule();
    }
    async function loaded() {
      if (!story.alive || photo.status !== 'loading') return;
      if (!image.naturalWidth) return failed();
      photo.status = 'decoding';
      try {
        if (typeof image.decode === 'function') await image.decode();
      } catch (_) {
        // A responsive source may change during decode. A loaded image remains
        // paintable; a failed resource must never replace a visible photograph.
        if (!image.complete || !image.naturalWidth) return failed();
      }
      if (!story.alive || story.failed || photo.status !== 'decoding') return;
      cleanup();
      photo.status = 'ready';
      prefetchNext(story);
      schedule();
    }
    photo.cleanup = cleanup;
    image.addEventListener('load', loaded);
    image.addEventListener('error', failed);
    if (template) template.replaceWith(fragment);
    if (image.complete) {
      if (image.naturalWidth) loaded();
      else failed();
    }
  }

  function photographicPosition(story, position) {
    if (!story.managed) return position;
    const left = Math.floor(position);
    const right = Math.ceil(position);
    loadPhoto(story, left);
    if (right !== left) loadPhoto(story, right);
    prefetchNext(story);
    if (story.photos[left]?.status === 'ready' && story.photos[right]?.status === 'ready') {
      story.displayed = Math.round(position);
      return position;
    }
    // Scrolling stays native even when the connection is slower than a gesture.
    // Keep the last decoded frame, rather than crossfading into an empty layer.
    const visible = [Math.round(position), story.displayed, left, right, 0].find((index) => story.photos[index]?.status === 'ready');
    if (visible !== undefined) story.displayed = visible;
    return story.displayed;
  }

  function resetStory(story, restore = true) {
    story.root.classList.remove('is-story-enhanced');
    story.root.style.removeProperty('--story-progress');
    story.frames.forEach((node) => {
      node.classList.remove('is-current');
      ['--frame-opacity', '--frame-scale', '--story-drift'].forEach((property) => node.style.removeProperty(property));
    });
    story.steps.forEach((node) => {
      node.classList.remove('is-current');
      node.style.removeProperty('--step-progress');
    });
    story.current = -1;
    if (restore) restorePhotos(story.root);
  }

  function framePosition(rects, center, height) {
    const boundaries = rects.slice(1).map((rect, index) => (rects[index].bottom + rect.top) / 2);
    for (let index = 0; index < boundaries.length; index++) {
      const boundary = boundaries[index];
      // A 20%-viewport crossfade follows actual scroll, not a timed CSS transition.
      // Short or irregular chapters cannot create overlapping transition bands.
      const previous = index ? (boundary - boundaries[index - 1]) / 2 : Infinity;
      const next = index < boundaries.length - 1 ? (boundaries[index + 1] - boundary) / 2 : Infinity;
      const half = Math.max(1, Math.min(height * 0.1, previous, next));
      if (center < boundary - half) return index;
      if (center <= boundary + half) return index + clamp((center - boundary + half) / (half * 2));
    }
    return rects.length - 1;
  }

  function update() {
    frame = null;
    if (!watchingViewport) return;
    const height = window.innerHeight || document.documentElement.clientHeight;
    const center = height / 2;

    // Read every chapter before writing styles, including a restored mid-page position.
    const measurements = Array.from(stories.values(), (story) => {
      const rects = motionEnabled ? story.steps.map((step) => step.getBoundingClientRect()) : [];
      const openingRect = story.header
        ? rects[story.steps.indexOf(story.opening)] || story.opening.getBoundingClientRect()
        : null;
      return { story, rects, openingRect };
    });
    measurements.forEach(({ story, rects, openingRect }) => {
      // The navigation stays responsive even when photographic motion is disabled.
      story.header?.classList.toggle('is-over-hero', openingRect.top >= -32);
      if (!motionEnabled || story.failed) return;
      const position = framePosition(rects, center, height);
      const current = Math.round(position);
      story.target = current;
      const photoPosition = photographicPosition(story, position);
      if (current !== story.current) {
        story.steps.forEach((node, index) => node.classList.toggle('is-current', index === current));
        story.current = current;
      }
      const start = rects[0].top;
      const end = rects[rects.length - 1].bottom;
      const progress = clamp((center - start) / Math.max(1, end - start));
      story.root.style.setProperty('--story-progress', progress.toFixed(4));
      rects.forEach((rect, index) => {
        const localProgress = clamp((center - rect.top) / Math.max(1, rect.bottom - rect.top));
        const node = story.frames[index];
        node.classList.toggle('is-current', index === Math.round(photoPosition));
        node.style.setProperty('--frame-opacity', Math.max(0, 1 - Math.abs(index - photoPosition)).toFixed(4));
        node.style.setProperty('--frame-scale', (1 + 0.075 * localProgress).toFixed(4));
        node.style.setProperty('--story-drift', localProgress.toFixed(4));
        story.steps[index].style.setProperty('--step-progress', localProgress.toFixed(4));
      });
    });
  }

  function schedule() {
    if (!watchingViewport || frame !== null) return;
    frame = window.requestAnimationFrame(update);
  }

  function syncMedia() {
    const hasAnimationFrame = typeof window.requestAnimationFrame === 'function';
    motionEnabled = Boolean(media?.matches && hasAnimationFrame && stories.size);
    const hasHeader = Array.from(stories.values()).some((story) => story.header);
    if (motionEnabled) stories.forEach((story) => {
      if (story.failed) resetStory(story);
      else {
        story.root.classList.add('is-story-enhanced');
        loadPhoto(story, 0);
      }
    });
    else stories.forEach(resetStory);
    if (!hasAnimationFrame || (!motionEnabled && !hasHeader)) {
      watchingViewport = false;
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('pageshow', schedule);
      cancelFrame();
      stories.forEach((story) => story.header?.classList.remove('is-over-hero'));
      return;
    }
    if (!watchingViewport) {
      watchingViewport = true;
      window.addEventListener('scroll', schedule, passive);
      window.addEventListener('resize', schedule, passive);
      window.addEventListener('pageshow', schedule, passive);
    }
    // Measure after the sticky class changes, before painting a restored anchor.
    cancelFrame();
    update();
  }

  function syncListeners() {
    const hasTargets = Boolean(stories.size);
    if (hasTargets && !watchingMedia && media) {
      if (typeof media.addEventListener === 'function') media.addEventListener('change', syncMedia);
      else if (typeof media.addListener === 'function') media.addListener(syncMedia);
      watchingMedia = true;
    } else if (!hasTargets && watchingMedia) {
      if (typeof media.removeEventListener === 'function') media.removeEventListener('change', syncMedia);
      else if (typeof media.removeListener === 'function') media.removeListener(syncMedia);
      watchingMedia = false;
    }
    syncMedia();
  }

  function matching(scope, selector) {
    const nodes = Array.from(scope.querySelectorAll(selector));
    if (scope.matches?.(selector)) nodes.unshift(scope);
    return nodes;
  }

  function init(scope = document) {
    matching(scope, '[data-at-home-story]').forEach((root) => {
      if (stories.has(root)) return;
      const stage = root.querySelector('[data-story-stage]');
      const frames = stage ? Array.from(stage.querySelectorAll('[data-story-frame]')) : [];
      const steps = Array.from(root.querySelectorAll('[data-story-step]'));
      if (frames.length < 2 || frames.length !== steps.length) {
        restorePhotos(root);
        return;
      }
      const opening = root.querySelector('.at-cinema__step--opening');
      const header = document.body?.dataset.page === 'index' && opening
        ? document.querySelector('.at-header') : null;
      const managed = Boolean(root.querySelector('template[data-story-media]'));
      const story = { root, frames, steps, opening, header, current: -1, target: -1, displayed: 0,
        managed, alive: true, failed: root.dataset.storyRecovered === 'true',
        photos: frames.map((frame) => ({ frame, image: null, status: managed ? 'idle' : 'ready', cleanup: null })) };
      stories.set(root, story);
    });
    syncListeners();
    stories.forEach((story) => { story.root.dataset.storyControllerReady = 'true'; });
  }

  function unload(event) {
    const scope = event.target;
    stories.forEach((story, root) => {
      if (scope === root || scope.contains(root)) {
        story.alive = false;
        story.photos.forEach((photo) => photo.cleanup?.());
        resetStory(story, false);
        story.header?.classList.remove('is-over-hero');
        stories.delete(root);
      }
    });
    syncListeners();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', unload);
  document.addEventListener('at:story-fallback', (event) => {
    const story = stories.get(event.target);
    if (!story) return;
    story.failed = true;
    story.photos.forEach((photo) => photo.cleanup?.());
    resetStory(story);
    syncMedia();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
})();
